/**
 * OurNet Group Helpers - Hierarchy and Cross-Linking Utilities
 *
 * Provides functions for:
 * - Hierarchical group navigation (breadcrumbs)
 * - Child group management
 * - Admin permission checks
 * - Content promotion between groups
 * - Related group linking
 */

import { supabaseFetch, isDemoReadActive } from '../lib/supabase';
import { demoGetChildGroups, demoGetGroupHierarchy } from '../data/demoReadService';

/**
 * Maximum hierarchy depth allowed
 */
const MAX_HIERARCHY_DEPTH = 3;

/**
 * Get the full hierarchy path for a group (for breadcrumbs)
 * Returns array from root to current group
 *
 * @param {string} groupId - UUID of the group
 * @returns {Promise<Array>} - Array of {id, name, hierarchy_level, depth}
 */
export async function getGroupHierarchy(groupId) {
    try {
        // Demo read target on → the client-side mirror (the public function cannot see demo rows).
        const { data, error } = isDemoReadActive()
            ? await demoGetGroupHierarchy(groupId)
            : await supabaseFetch.rpc('get_group_hierarchy', { group_uuid: groupId });

        if (error) {
            console.error('Error fetching group hierarchy:', error);
            return [];
        }

        return data || [];
    } catch (error) {
        console.error('Exception in getGroupHierarchy:', error);
        return [];
    }
}

/**
 * Get direct children of a parent group
 * Uses supabaseFetch (supabase client can hang)
 *
 * @param {string} parentId - UUID of the parent group
 * @returns {Promise<Array>} - Array of child group objects
 */
export async function getChildGroups(parentId) {
    try {
        // Demo read target on → the client-side mirror (the public function cannot see demo rows).
        const { data, error } = isDemoReadActive()
            ? await demoGetChildGroups(parentId)
            : await supabaseFetch.rpc('get_child_groups', { parent_uuid: parentId });

        if (error) {
            console.error('Error fetching child groups:', error);
            return [];
        }

        return data || [];
    } catch (error) {
        console.error('Exception in getChildGroups:', error);
        return [];
    }
}

/**
 * Get federation member groups (groups whose federation_id points to this group)
 * Separate from nested hierarchy (parent_group_id) — federation membership is unlimited.
 *
 * Display and the un-designation guard are different questions (Federation 4C §6): display
 * must never show a soft-deleted group as a member, but the guard's job is integrity — it
 * must see a dangling pointer, or un-designation would succeed while soft-deleted members
 * still point at a group that is no longer a federation. Filter on read, never clear on
 * write — soft delete is reversible, clearing federation_id is not.
 *
 * @param {string} federationId - UUID of the federation group
 * @param {object} [options]
 * @param {boolean} [options.includeDeleted=false] - true for the guard's count; false (default) for display
 * @param {string} [options.select] - override the default column list (e.g. 'id' when only a count is needed)
 * @returns {Promise<Array>} - Array of member group objects
 */
export async function getFederationMembers(federationId, { includeDeleted = false, select } = {}) {
    try {
        const queryOptions = {
            select: select || 'id,name,description,logo_url,cover_image_url,hero_image_thumbnail_url,member_count,owner_id,is_private,auto_accept_members,visible_to_parent,hierarchy_level',
            eq: { federation_id: federationId },
            order: 'name.asc'
        };
        if (!includeDeleted) {
            queryOptions.is = { deleted_at: null };
        }
        const { data, error } = await supabaseFetch.query('communities', queryOptions);

        if (error) {
            console.error('Error fetching federation members:', error);
            return [];
        }

        return data || [];
    } catch (error) {
        console.error('Exception in getFederationMembers:', error);
        return [];
    }
}

/**
 * Resolve how a group should be shared, given its current visibility.
 *
 * This is the seam a member-facing "Share this group" control calls through — it does not
 * itself know about group types. Today it only handles the public case: the cold-loadable
 * hash route (`App.js`'s `parseHash`/`handleHashNavigation` 'group' case) already renders a
 * public group's Home surface, with a working Join control, for a logged-out visitor —
 * confirmed live, no smart_links row required (see
 * docs/briefs/2026-09-05-groups-share-requesttojoin-and-federation-state-probe-RESULTS.md, A1/A5).
 * There is no "publicly-visible private" state in the schema yet (same probe, A2), so a private
 * group returns `isShareable: false` for now — this is where that branch plugs in once it exists.
 *
 * @param {object} group - the group record (needs id, is_private)
 * @returns {{ shareUrl: string|null, isShareable: boolean }}
 */
export function getGroupShareUrl(group) {
    if (!group?.id || group.is_private) {
        return { shareUrl: null, isShareable: false };
    }
    return {
        shareUrl: `${window.location.origin}/#/group/${group.id}`,
        isShareable: true
    };
}

/**
 * Get sibling groups (other groups with the same parent)
 * Excludes the current group if provided
 *
 * @param {string} parentId - UUID of the parent group (null for top-level siblings)
 * @param {string} excludeGroupId - Optional group ID to exclude from results
 * @returns {Promise<Array>} - Array of sibling group objects
 */
export async function getSiblingGroups(parentId, excludeGroupId = null) {
    try {
        // supabaseFetch (not a raw fetch): it sends the user's token rather than always the anon
        // key, and follows the demo read target (Phase B1). is/neq now exist on the wrapper.
        // Note: no caller of getSiblingGroups exists in src/ (18 Sep 2026).
        const { data, error } = await supabaseFetch.query('communities', {
            select: 'id,name,hierarchy_level,logo_url,member_count',
            eq: parentId ? { community_type: 'group', parent_group_id: parentId } : { community_type: 'group' },
            is: parentId ? {} : { parent_group_id: null },
            neq: excludeGroupId ? { id: excludeGroupId } : {},
            order: 'name.asc',
            limit: 10
        });

        if (error) {
            console.error('Error fetching sibling groups:', error);
            return [];
        }

        return data || [];
    } catch (error) {
        console.error('Exception in getSiblingGroups:', error);
        return [];
    }
}

/**
 * Check if user can create a subgroup under the specified parent
 * Requirements: User must be admin/owner AND parent level < 3
 *
 * @param {string} parentId - UUID of the parent group
 * @param {string} userId - UUID of the user
 * @returns {Promise<boolean>} - True if user can create subgroup
 */
export async function canCreateSubgroup(parentId, userId) {
    try {
        const { data, error } = await supabaseFetch.rpc('can_create_subgroup', {
            parent_uuid: parentId,
            user_uuid: userId
        });

        if (error) {
            console.error('Error checking subgroup permission:', error);
            return false;
        }

        return data === true;
    } catch (error) {
        console.error('Exception in canCreateSubgroup:', error);
        return false;
    }
}

/**
 * Maximum total descendants allowed in a group tree (free tier).
 * Root group doesn't count — so a tree can have root + 9 = 10 groups total.
 * This limit only applies when root's pricing_tier = 'free'.
 */
export const MAX_TREE_DESCENDANTS_FREE = 9;

/**
 * The translation key (groups namespace) naming a group's type, in Michael's three names (brief
 * 2026-10-04 R1): Fully Public / Private – Request to join / Fully Private – Invitation only. A
 * group object read without group_access falls back to the plain Public / Private label.
 */
export function groupTypeLabelKey(group) {
  switch (group?.group_access) {
    case 'public': return 'group_type_public';
    case 'private_findable': return 'group_type_private_findable';
    case 'private_invitation': return 'group_type_private_invitation';
    default: return group?.is_private ? 'visibility_private' : 'visibility_public';
  }
}

/**
 * Get groups where user is admin/owner and can create subgroups
 * Used for parent group dropdown in AddEditGroup
 * Checks tree-level descendant count (max 9 for free tier)
 *
 * @param {string} userId - UUID of the user
 * @returns {Promise<Array>} - Array of eligible parent groups with tree_descendant_count
 */
export async function getEligibleParentGroups(userId) {
    try {
        const { data, error } = await supabaseFetch.query('community_members', {
            select: 'community_id, communities!inner(id, name, hierarchy_level, logo_url)',
            eq: { user_id: userId },
            inFilter: { role: ['owner', 'admin'] }
        });

        if (error) {
            console.error('Error fetching eligible parent groups:', error);
            return [];
        }

        // Filter to only groups with level < 3 (can have children)
        const potentialParents = (data || [])
            .filter(item => item.communities && item.communities.hierarchy_level < MAX_HIERARCHY_DEPTH)
            .map(item => ({
                id: item.communities.id,
                name: item.communities.name,
                hierarchy_level: item.communities.hierarchy_level,
                logo_url: item.communities.logo_url
            }));

        const parentIds = potentialParents.map(p => p.id);
        if (parentIds.length === 0) return [];

        // Get tree descendant counts for all potential parents in one batch RPC
        const { data: treeCounts, error: countError } = await supabaseFetch.rpc(
            'get_tree_descendant_counts',
            { group_uuids: parentIds }
        );

        if (countError) {
            console.error('Error fetching tree descendant counts:', countError);
            return potentialParents.map(p => ({ ...p, tree_descendant_count: 0, is_full: false, pricing_tier: 'free' }));
        }

        // Build lookup: group_id -> { root_id, descendant_count, pricing_tier }
        const countMap = {};
        (treeCounts || []).forEach(item => {
            countMap[item.group_id] = item;
        });

        return potentialParents.map(p => {
            const treeInfo = countMap[p.id] || {};
            const count = treeInfo.descendant_count || 0;
            const tier = treeInfo.pricing_tier || 'free';
            return {
                ...p,
                tree_descendant_count: count,
                pricing_tier: tier,
                is_full: tier === 'free' && count >= MAX_TREE_DESCENDANTS_FREE
            };
        });
    } catch (error) {
        console.error('Exception in getEligibleParentGroups:', error);
        return [];
    }
}

/**
 * Promote content from current group to parent group
 * Admin-only operation (either lower group admin OR parent group admin)
 *
 * @param {string} contentId - UUID of the article/content
 * @param {string} currentGroupId - UUID of the current group
 * @param {string} userId - UUID of the user performing the action
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function promoteContentToParent(contentId, currentGroupId, userId) {
    try {
        // First, get the parent group
        const { data: currentGroup, error: groupError } = await supabaseFetch.query(
            'communities',
            { select: 'id, parent_group_id', eq: { id: currentGroupId }, single: true }
        );

        if (groupError || !currentGroup?.parent_group_id) {
            return { success: false, error: 'Group has no parent to promote to' };
        }

        const parentGroupId = currentGroup.parent_group_id;

        // Check if user is admin of either current or parent group
        const { data: membership, error: memberError } = await supabaseFetch.query(
            'community_members',
            {
                select: 'community_id, role',
                eq: { user_id: userId },
                inFilter: { community_id: [currentGroupId, parentGroupId], role: ['owner', 'admin'] }
            }
        );

        if (memberError || !membership || membership.length === 0) {
            return { success: false, error: 'User is not authorized to promote content' };
        }

        // Update the article's community_id to the parent
        const { error: updateError } = await supabaseFetch.update(
            'articles',
            { community_id: parentGroupId },
            { eq: { id: contentId } }
        );

        if (updateError) {
            console.error('Error promoting content:', updateError);
            return { success: false, error: 'Failed to promote content' };
        }

        return { success: true };
    } catch (error) {
        console.error('Exception in promoteContentToParent:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Get related groups (bidirectional links) - members only
 *
 * @param {string} groupId - UUID of the group
 * @param {string} userId - UUID of the requesting user
 * @returns {Promise<Array>} - Array of related group objects
 */
export async function getRelatedGroups(groupId, userId) {
    try {
        const { data, error } = await supabaseFetch.rpc('get_related_groups', {
            group_uuid: groupId,
            user_uuid: userId
        });

        if (error) {
            console.error('Error fetching related groups:', error);
            return [];
        }

        return data || [];
    } catch (error) {
        console.error('Exception in getRelatedGroups:', error);
        return [];
    }
}

/**
 * Add a link between two groups
 * Creates link in one direction; bidirectional display handled by queries
 *
 * @param {string} sourceId - UUID of the source group
 * @param {string} linkedId - UUID of the group to link to
 * @param {string} userId - UUID of the user creating the link
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function addGroupLink(sourceId, linkedId, userId) {
    try {
        // Prevent self-linking
        if (sourceId === linkedId) {
            return { success: false, error: 'Cannot link a group to itself' };
        }

        // Check if link already exists in either direction using direct REST API
        const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
        const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
        const authToken = await supabaseFetch.getAuthToken();
        const token = authToken || SUPABASE_KEY;

        const checkParams = new URLSearchParams();
        checkParams.append('select', 'id');
        checkParams.append('or', `(and(source_group_id.eq.${sourceId},linked_group_id.eq.${linkedId}),and(source_group_id.eq.${linkedId},linked_group_id.eq.${sourceId}))`);

        const checkResponse = await fetch(
            `${SUPABASE_URL}/rest/v1/group_links?${checkParams.toString()}`,
            {
                headers: {
                    'apikey': SUPABASE_KEY,
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            }
        );

        if (!checkResponse.ok) {
            console.error('Error checking existing links:', await checkResponse.text());
            return { success: false, error: 'Failed to check existing links' };
        }

        const existingLinks = await checkResponse.json();
        if (existingLinks && existingLinks.length > 0) {
            return { success: false, error: 'Groups are already linked' };
        }

        // Create the link
        const { error } = await supabaseFetch.insert('group_links', {
            source_group_id: sourceId,
            linked_group_id: linkedId,
            created_by: userId
        });

        if (error) {
            console.error('Error creating group link:', error);
            return { success: false, error: error.message };
        }

        return { success: true };
    } catch (error) {
        console.error('Exception in addGroupLink:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Remove a link between two groups
 *
 * @param {string} sourceId - UUID of the source group
 * @param {string} linkedId - UUID of the linked group
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function removeGroupLink(sourceId, linkedId) {
    try {
        // Delete link in either direction using direct REST API
        const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
        const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
        const authToken = await supabaseFetch.getAuthToken();
        const token = authToken || SUPABASE_KEY;

        const deleteParams = new URLSearchParams();
        deleteParams.append('or', `(and(source_group_id.eq.${sourceId},linked_group_id.eq.${linkedId}),and(source_group_id.eq.${linkedId},linked_group_id.eq.${sourceId}))`);

        const response = await fetch(
            `${SUPABASE_URL}/rest/v1/group_links?${deleteParams.toString()}`,
            {
                method: 'DELETE',
                headers: {
                    'apikey': SUPABASE_KEY,
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            }
        );

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Error removing group link:', errorText);
            return { success: false, error: errorText };
        }

        return { success: true };
    } catch (error) {
        console.error('Exception in removeGroupLink:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Check if user is an admin of the specified group
 *
 * @param {string} groupId - UUID of the group
 * @param {string} userId - UUID of the user
 * @returns {Promise<boolean>}
 */
export async function isGroupAdmin(groupId, userId) {
    try {
        const { data, error } = await supabaseFetch.query(
            'community_members',
            {
                select: 'role',
                eq: { community_id: groupId, user_id: userId },
                inFilter: { role: ['owner', 'admin'] },
                single: true
            }
        );

        if (error) {
            // Not found is expected for non-admins
            if (error.status === 406 || error.message?.includes('not found')) {
                return false;
            }
            console.error('Error checking admin status:', error);
            return false;
        }

        return !!data;
    } catch (error) {
        console.error('Exception in isGroupAdmin:', error);
        return false;
    }
}

/**
 * Get direct subgroup count for a group (used for tree expansion, NOT limit enforcement).
 * For limit checks, use getTreeDescendantCount() instead.
 *
 * @param {string} groupId - UUID of the group
 * @returns {Promise<number>}
 */
export async function getSubgroupCount(groupId) {
    try {
        const { data, error } = await supabaseFetch.query('communities', {
            select: 'id',
            eq: { parent_group_id: groupId }
        });

        if (error) {
            console.error('Error counting subgroups:', error);
            return 0;
        }

        return (data || []).length;
    } catch (error) {
        console.error('Exception in getSubgroupCount:', error);
        return 0;
    }
}

/**
 * Get total descendant count for a group's tree, plus pricing info.
 * Finds the Level 1 root ancestor and counts all descendants under it.
 * Used for race-condition protection when creating sub-groups.
 *
 * @param {string} groupId - UUID of any group in the tree
 * @returns {Promise<{descendant_count: number, pricing_tier: string, max_members: number|null}>}
 */
export async function getTreeDescendantCount(groupId) {
    try {
        const { data, error } = await supabaseFetch.rpc('get_tree_descendant_count', {
            group_uuid: groupId
        });

        if (error) {
            console.error('Error counting tree descendants:', error);
            return { descendant_count: 0, pricing_tier: 'free', max_members: null };
        }

        const row = Array.isArray(data) ? data[0] : data;
        return {
            descendant_count: row?.descendant_count || 0,
            pricing_tier: row?.pricing_tier || 'free',
            max_members: row?.max_members ?? null
        };
    } catch (error) {
        console.error('Exception in getTreeDescendantCount:', error);
        return { descendant_count: 0, pricing_tier: 'free', max_members: null };
    }
}

/**
 * Get the full group tree starting from the root of a group's hierarchy
 * Returns a tree structure with children nested under each node
 *
 * @param {string} groupId - UUID of any group in the tree
 * @returns {Promise<Object|null>} - Tree structure: { id, name, children: [...] }
 */
export async function getFullGroupTree(groupId) {
    try {
        // First, get the hierarchy path to find the root
        const hierarchy = await getGroupHierarchy(groupId);
        if (!hierarchy || hierarchy.length === 0) {
            // Group has no hierarchy, fetch just this group
            const { data: singleGroup } = await supabaseFetch.query('communities', {
                select: 'id, name, hierarchy_level, parent_group_id',
                eq: { id: groupId },
                single: true
            });
            return singleGroup ? { ...singleGroup, children: [] } : null;
        }

        // Root is the first item in hierarchy (depth 1)
        const rootId = hierarchy[0]?.id;
        if (!rootId) return null;

        // Fetch all groups that belong to this tree (share the same root)
        // We'll fetch recursively by getting all descendants
        const allGroups = await fetchAllDescendants(rootId);

        // Build the tree structure
        const tree = buildTree(allGroups, null);
        return tree.length > 0 ? tree[0] : null;
    } catch (error) {
        console.error('Exception in getFullGroupTree:', error);
        return null;
    }
}

/**
 * Fetch all descendants of a group (including the group itself)
 * @param {string} rootId - UUID of the root group
 * @returns {Promise<Array>}
 */
async function fetchAllDescendants(rootId) {
    // First fetch the root
    const { data: rootGroup } = await supabaseFetch.query('communities', {
        select: 'id, name, hierarchy_level, parent_group_id',
        eq: { id: rootId },
        single: true
    });

    if (!rootGroup) return [];

    const allGroups = [rootGroup];
    let currentLevelIds = [rootId];

    // Fetch up to 3 levels deep (max hierarchy)
    for (let level = 1; level <= 3 && currentLevelIds.length > 0; level++) {
        // Fetch all children of current level (supabaseFetch — follows the demo read target)
        const { data: children, error } = await supabaseFetch.query('communities', {
            select: 'id,name,hierarchy_level,parent_group_id',
            eq: { community_type: 'group' },
            inFilter: { parent_group_id: currentLevelIds },
            order: 'name.asc'
        });

        if (error) break;
        if (!children || children.length === 0) break;

        allGroups.push(...children);
        currentLevelIds = children.map(c => c.id);
    }

    return allGroups;
}

/**
 * Build a tree structure from flat array of groups
 * @param {Array} groups - Flat array of group objects
 * @param {string|null} parentId - Parent ID to filter by
 * @returns {Array} - Array of tree nodes with children
 */
function buildTree(groups, parentId) {
    return groups
        .filter(g => g.parent_group_id === parentId)
        .map(g => ({
            ...g,
            children: buildTree(groups, g.id)
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Get a group with its parent information
 *
 * @param {string} groupId - UUID of the group
 * @returns {Promise<Object|null>}
 */
export async function getGroupWithParent(groupId) {
    try {
        // Fetch the group itself (direct fetch, bypasses JS client which can hang)
        const { data: group, error } = await supabaseFetch.query('communities', {
            select: '*',
            eq: { id: groupId },
            single: true
        });

        if (error) {
            console.error('Error fetching group with parent:', error);
            return null;
        }

        if (!group) return null;

        // If group has a parent, fetch parent info separately
        if (group.parent_group_id) {
            const { data: parent } = await supabaseFetch.query('communities', {
                select: 'id, name, hierarchy_level, logo_url',
                eq: { id: group.parent_group_id },
                single: true
            });
            group.parent = parent || null;
        } else {
            group.parent = null;
        }

        return group;
    } catch (error) {
        console.error('Exception in getGroupWithParent:', error);
        return null;
    }
}

/**
 * Check membership status for a user across multiple groups in one query.
 * Returns a Map of groupId → role (or undefined if not a member).
 *
 * @param {string} userId - UUID of the user
 * @param {string[]} groupIds - Array of group UUIDs to check
 * @returns {Promise<Object>} - { [groupId]: role } e.g. { 'abc': 'member', 'def': 'owner' }
 */
export async function checkBatchMembership(userId, groupIds) {
    if (!userId || !groupIds || groupIds.length === 0) return {};

    try {
        const { data, error } = await supabaseFetch.query('community_members', {
            select: 'community_id, role',
            eq: { user_id: userId },
            inFilter: { community_id: groupIds }
        });

        if (error) {
            console.error('Error checking batch membership:', error);
            return {};
        }

        const membershipMap = {};
        (data || []).forEach(row => {
            membershipMap[row.community_id] = row.role;
        });
        return membershipMap;
    } catch (error) {
        console.error('Exception in checkBatchMembership:', error);
        return {};
    }
}
